import React, { useState, useEffect, useRef, useCallback } from 'react';
import { relationshipsApi } from '../api';
import type { Person } from '../types/person';
import RelationshipForm from './RelationshipForm';

interface SimNode {
  id: number;
  name: string;
  color: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface SimEdge {
  source: number;
  target: number;
  relationship_type: string;
  strength: number;
}

interface RelationshipGraphProps {
  people: Person[];
  onPersonSelect: (personId: number) => void;
  onRefresh: () => void;
}

const RelationshipGraph: React.FC<RelationshipGraphProps> = ({ people, onPersonSelect, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<SimNode[]>([]);
  const edgesRef = useRef<SimEdge[]>([]);
  const animRef = useRef<number>(0);
  const dragRef = useRef<{ nodeId: number | null; offsetX: number; offsetY: number }>({ nodeId: null, offsetX: 0, offsetY: 0 });
  const dprRef = useRef(1);

  const loadGraphData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await relationshipsApi.getGraph();
      const { nodes, edges } = response.data;
      const canvas = canvasRef.current;
      const w = canvas?.offsetWidth || 360;
      const h = canvas?.offsetHeight || 500;
      const cx = w / 2;
      const cy = h / 2;

      nodesRef.current = (nodes || []).map((n: any, i: number) => {
        const angle = (2 * Math.PI * i) / (nodes.length || 1);
        const r = Math.min(w, h) * 0.3;
        return {
          id: n.id,
          name: n.name,
          color: n.color || '#667eea',
          x: cx + r * Math.cos(angle) + (Math.random() - 0.5) * 20,
          y: cy + r * Math.sin(angle) + (Math.random() - 0.5) * 20,
          vx: 0,
          vy: 0,
        };
      });
      edgesRef.current = (edges || []).map((e: any) => ({
        source: e.source,
        target: e.target,
        relationship_type: e.relationship_type,
        strength: e.strength || 1,
      }));
    } catch (err) {
      console.error('加载图谱失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGraphData(); }, [loadGraphData]);

  const simulate = useCallback(() => {
    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const light = canvas.closest('[data-web-theme]')?.getAttribute('data-web-theme') === 'light';

    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const REPULSION = 3000;
    const SPRING_K = 0.008;
    const SPRING_LEN = 120;
    const DAMPING = 0.85;
    const CENTER_PULL = 0.002;

    for (const n of nodes) {
      if (dragRef.current.nodeId === n.id) continue;
      let fx = 0, fy = 0;

      for (const m of nodes) {
        if (m.id === n.id) continue;
        const dx = n.x - m.x;
        const dy = n.y - m.y;
        const dist = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const force = REPULSION / (dist * dist);
        fx += (dx / dist) * force;
        fy += (dy / dist) * force;
      }

      for (const e of edges) {
        let other: SimNode | undefined;
        if (e.source === n.id) other = nodes.find(nn => nn.id === e.target);
        else if (e.target === n.id) other = nodes.find(nn => nn.id === e.source);
        if (!other) continue;
        const dx = other.x - n.x;
        const dy = other.y - n.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const force = SPRING_K * (dist - SPRING_LEN);
        fx += (dx / dist) * force;
        fy += (dy / dist) * force;
      }

      fx += (w / 2 - n.x) * CENTER_PULL;
      fy += (h / 2 - n.y) * CENTER_PULL;

      n.vx = (n.vx + fx) * DAMPING;
      n.vy = (n.vy + fy) * DAMPING;
      n.x = Math.max(42, Math.min(w - 42, n.x + n.vx));
      n.y = Math.max(32, Math.min(h - 50, n.y + n.vy));
    }

    ctx.clearRect(0, 0, w, h);

    for (const e of edges) {
      const s = nodes.find(n => n.id === e.source);
      const t = nodes.find(n => n.id === e.target);
      if (!s || !t) continue;

      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = light
        ? `rgba(83,109,96,${0.25 + e.strength * 0.3})`
        : `rgba(148,163,184,${0.15 + e.strength * 0.3})`;
      ctx.lineWidth = Math.max(1, e.strength * 2);
      ctx.stroke();

      const mx = (s.x + t.x) / 2;
      const my = (s.y + t.y) / 2;
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = light ? '#536d60' : 'rgba(148,163,184,0.6)';
      ctx.textAlign = 'center';
      ctx.fillText(e.relationship_type, mx, my - 4);
    }

    for (const n of nodes) {
      ctx.beginPath();
      ctx.arc(n.x, n.y, 24, 0, Math.PI * 2);
      ctx.fillStyle = n.color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(n.name.substring(0, 2), n.x, n.y);

      ctx.font = '11px system-ui, sans-serif';
      ctx.fillStyle = light ? '#21372e' : 'rgba(226,232,240,0.8)';
      ctx.fillText(n.name, n.x, n.y + 36);
    }

    const totalV = nodes.reduce((s, n) => s + Math.abs(n.vx) + Math.abs(n.vy), 0);
    if (totalV > 0.1 || dragRef.current.nodeId !== null) {
      animRef.current = requestAnimationFrame(simulate);
    } else {
      animRef.current = 0;
    }
  }, []);

  useEffect(() => {
    if (!loading && nodesRef.current.length > 0) {
      animRef.current = requestAnimationFrame(simulate);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [loading, simulate]);

  useEffect(() => {
    const shell = canvasRef.current?.closest('[data-web-theme]');
    if (!shell) return;
    // Redraw a settled canvas when the web skin changes, without reloading data.
    const observer = new MutationObserver(() => {
      if (!animRef.current) animRef.current = requestAnimationFrame(simulate);
    });
    observer.observe(shell, { attributes: true, attributeFilter: ['data-web-theme'] });
    return () => observer.disconnect();
  }, [loading, simulate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      nodesRef.current.forEach(node => {
        node.x = Math.max(42, Math.min(w - 42, node.x));
        node.y = Math.max(32, Math.min(h - 50, node.y));
        node.vx += 0.1;
      });
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = requestAnimationFrame(simulate);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [loading, simulate]);

  const getNodeAt = (ex: number, ey: number): SimNode | undefined => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const rect = canvas.getBoundingClientRect();
    const x = ex - rect.left;
    const y = ey - rect.top;
    return nodesRef.current.find(n => Math.sqrt((n.x - x) ** 2 + (n.y - y) ** 2) < 28);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    const node = getNodeAt(e.clientX, e.clientY);
    if (!node) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    dragRef.current = { nodeId: node.id, offsetX: e.clientX - rect.left - node.x, offsetY: e.clientY - rect.top - node.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragRef.current.nodeId === null) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const node = nodesRef.current.find(n => n.id === dragRef.current.nodeId);
    if (!node) return;
    node.x = e.clientX - rect.left - dragRef.current.offsetX;
    node.y = e.clientY - rect.top - dragRef.current.offsetY;
    node.vx = 0;
    node.vy = 0;
    if (!animRef.current) animRef.current = requestAnimationFrame(simulate);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (dragRef.current.nodeId !== null) {
      const node = getNodeAt(e.clientX, e.clientY);
      if (node && node.id === dragRef.current.nodeId) {
        const moved = Math.abs(dragRef.current.offsetX) < 3 && Math.abs(dragRef.current.offsetY) < 3;
        if (!moved) { /* dragged, don't navigate */ }
      }
    }
    dragRef.current = { nodeId: null, offsetX: 0, offsetY: 0 };
  };

  const handleClick = (e: React.MouseEvent) => {
    if (dragRef.current.nodeId !== null) return;
    const node = getNodeAt(e.clientX, e.clientY);
    if (node) onPersonSelect(node.id);
  };

  if (loading) {
    return <div style={{ padding:40, textAlign:'center', color:'var(--text-tertiary)' }}>加载图谱中...</div>;
  }

  if (nodesRef.current.length === 0) {
    return (
      <div style={{ textAlign:'center', padding:40 }}>
        <p style={{ color:'var(--text-tertiary)', marginBottom:16 }}>还没有人物关系数据</p>
        <button className="rg-add-btn" onClick={() => setShowForm(true)}>+ 添加关系</button>
      </div>
    );
  }

  return (
    <div className="rg-container">
      <div className="rg-toolbar">
        <span className="rg-title">关系图谱</span>
        <button className="rg-add-btn" onClick={() => setShowForm(true)}>+ 添加关系</button>
      </div>

      {showForm && (
        <RelationshipForm people={people} onClose={() => setShowForm(false)} onSubmit={() => { setShowForm(false); loadGraphData(); onRefresh(); }} />
      )}

      <div className="rg-canvas-wrap">
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ width:'100%', height:500, touchAction:'pan-y', cursor:'grab' }}
        />
      </div>
      <div className="rg-hint">拖拽节点调整位置 · 点击查看详情</div>

      <style>{`
        .rg-container { animation:plFade 0.3s ease; padding:18px; border:1px solid rgba(148,163,184,.09); border-radius:20px; background:linear-gradient(155deg,rgba(15,23,42,.84),rgba(7,13,27,.9)); box-shadow:0 18px 48px rgba(0,0,0,.16); }
        @keyframes plFade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .rg-toolbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .rg-title { font-size:15px; font-weight:800; color:#e2e8f0; }
        .rg-add-btn { padding:9px 15px; border:1px solid rgba(45,212,191,.18); border-radius:12px; background:linear-gradient(135deg,#7c3aed,#0f766e); color:#fff; font-size:12px; font-weight:750; cursor:pointer; font-family:inherit; }
        .rg-add-btn:active { transform:scale(0.95); }
        .rg-canvas-wrap { background:radial-gradient(circle at 50% 45%,rgba(76,29,149,.18),transparent 38%),radial-gradient(circle at 72% 72%,rgba(13,148,136,.12),transparent 30%),rgba(2,6,23,.5); border:1px solid rgba(167,139,250,.12); border-radius:16px; overflow:hidden; }
        .rg-hint { font-size:11px; color:#64748b; text-align:center; margin-top:10px; }
        @media (max-width:760px) {
          .rg-container { padding:12px; border-radius:16px; }
          .rg-canvas-wrap canvas { height:clamp(320px,52dvh,440px) !important; }
        }
      `}</style>
    </div>
  );
};

export default RelationshipGraph;
